/*
 * PROFILE: "EVERYTHING WE KNOW", AND WHERE EACH FACT CAME FROM.
 *
 * The one tab where repetition is allowed (the brief, "Who owns what"): every
 * stored fact about the person and the company, in groups, with a source chip on
 * every row, a pencil where a write path exists, a search field ("Search 31
 * facts") and the empty fields behind "Show empty (n)".
 *
 * A REGISTRY, NOT A DUMP. contacts.properties carries about 398 HubSpot keys on an
 * imported record, and printing them would bury the thirty that matter. So the
 * fields are listed here, by hand, under our own keys, each reading the column or
 * property its readers already use (src/leadViews.js, the Leads page's
 * LEAD_COLUMNS, src/enrich/firmographics.js). The frozen HubSpot facts that are
 * worth reading get their own group with our own names; nothing named hs_* is ever
 * shown as itself. test/record-profile-model.test.js pins that.
 *
 * SOURCE CHIPS ARE INFERRED (reality.txt [11]): no per-field provenance is stored
 * ("Per-field source and date" stays a Proposed tag). A fact reads "Gmail · Sep 15"
 * when the warm-intro triage wrote it, "Webflow form" when the form did, "Apollo"
 * for the enrichment keys on a record with no HubSpot id, "HubSpot export" for the
 * frozen keys, "You · Sep 30" / "Automatic · Sep 16" for moves read off the stage
 * history, "Computed" for what is worked out on read, and "source unknown" for
 * everything else. Never "You" for a value nobody can say he typed.
 *
 *   profileModel({ contact, company, act, timeline, score, query, showEmpty })
 *     → { groups, facts, empty, searchLabel, showEmptyLabel }
 */
import { formAnswers, formMessage, latestFormSubmission } from "../formAnswers.mjs";
import { prettyUrl } from "../recordModel.mjs";
import { shortDate, enumWords, fullName, moneyShort, daysBetween, plural, toDate } from "./format.mjs";
import { STAGE_LABELS, lastTransition, pointOfContact } from "./situation.mjs";
import { dealRows } from "./moneyModel.mjs";

export const GROUPS = [
  { id: "person", label: "Person" },
  { id: "company", label: "Company" },
  { id: "arrived", label: "How they arrived" },
  { id: "told", label: "What they told us" },
  { id: "deal", label: "Deal and money" },
  { id: "history", label: "Relationship history" },
  { id: "linkedin", label: "LinkedIn ads" },
  { id: "hubspot", label: "HubSpot export (frozen)" },
  { id: "other", label: "Other" },
];

const LIFECYCLE = {
  lead: "lead",
  marketingqualifiedlead: "MQL",
  salesqualifiedlead: "SQL",
  opportunity: "opportunity",
  customer: "customer",
  subscriber: "subscriber",
  evangelist: "evangelist",
  other: "other",
};
const STATUS = {
  prospect: "Prospect",
  client: "Client",
  churned: "Churned",
  lost: "Lost",
  recycled: "Parked",
  not_a_fit: "Not a fit",
  vendor: "Vendor",
  advisory: "Advisory",
};
const SOURCES = { linkedin: "LinkedIn", referral: "Referral", organic: "Organic" };
const CHANNELS = { website_form: "Form", intro_email: "Intro", linkedin_dm: "LinkedIn DM", whatsapp_dm: "WhatsApp DM", other: "Other" };

const blank = (v) =>
  v === null || v === undefined || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && !v.length);
const props = (o) => (o && o.properties && typeof o.properties === "object" ? o.properties : {});

/* ------------------------------------------------------------ sources */

function chip(text) {
  return text ? { text } : null;
}

function arrivalChip(contact, kind) {
  const c = contact || {};
  const day = c.created_at ? shortDate(c.created_at) : null;
  if (c.lead_channel === "intro_email" && (kind === "intro" || kind === "person")) return chip(day ? `Gmail · ${day}` : "Gmail");
  if (c.lead_channel === "website_form" && (kind === "form" || kind === "person")) return chip(day ? `Webflow form · ${day}` : "Webflow form");
  if (c.hubspot_id && kind === "person") return chip("HubSpot export");
  return null;
}

function apolloChip(company) {
  return company && company.hubspot_id ? chip("source unknown") : chip("Apollo");
}

/** "You · Sep 30", "Automatic · Sep 16", from a stage_transitions row. */
export function transitionChip(row) {
  if (!row) return chip("source unknown");
  const day = row.occurred_at ? shortDate(row.occurred_at) : null;
  const who = row.source === "app" ? "You" : row.source === "automation" || row.source === "auditor" ? "Automatic" : row.source === "mcp" ? "Claude" : row.source === "hubspot_import" ? "HubSpot export" : null;
  if (!who) return chip("source unknown");
  return chip(day ? `${who} · ${day}` : who);
}

/** The newest lifecycle move to the contact's current stage, off the company timeline. */
function lifecycleRow(contact, timeline) {
  const rows = (timeline || []).filter(
    (r) => r && r.item_type === "stage_transition" && r.subtype === "lifecycle_stage" && contact && String(r.contact_id) === String(contact.id) && r.to_value === contact.lifecycle_stage
  );
  return rows[0] || null;
}

/* ------------------------------------------------------------ the registry */

/*
 * Each field: { key, group, label, read(ctx) → value | null, edit, source(ctx) }.
 *   edit   { target: 'contact' | 'company', field } for a column on PATCH,
 *          { target, prop } for a key through properties_set (PR #154's
 *          allowlist), { target: 'contact', emails: true } for POST /emails, or
 *          null where nothing can write it (lead_channel is write-once; the
 *          frozen HubSpot facts are frozen).
 */
export const FIELDS = [
  // --- Person ---
  { key: "name", group: "person", label: "Name", read: ({ contact }) => fullName(contact && { first_name: contact.first_name, last_name: contact.last_name }), edit: { target: "contact", field: "name" }, source: ({ contact }) => arrivalChip(contact, "person") },
  { key: "title", group: "person", label: "Title", read: ({ contact }) => contact && contact.job_title, edit: { target: "contact", field: "job_title" }, source: ({ contact }) => (contact && contact.hubspot_id ? chip("HubSpot export") : chip("source unknown")) },
  { key: "email", group: "person", label: "Email", read: ({ contact }) => contact && contact.email, edit: { target: "contact", field: "email" }, source: ({ contact }) => arrivalChip(contact, "person") },
  { key: "other_emails", group: "person", label: "Other emails", read: ({ contact }) => { const a = (props(contact).alt_emails || []).filter((e) => typeof e === "string"); return a.length ? a.join(", ") : null; }, edit: { target: "contact", emails: true }, source: () => chip("source unknown") },
  { key: "phone", group: "person", label: "Phone", read: ({ contact }) => contact && contact.phone, edit: { target: "contact", field: "phone" }, source: () => chip("source unknown") },
  // The person's name as the link, as 1f draws it ("Shahar Bahat ↗"): a profile
  // address is a slug with whatever its owner typed in it, never easier to read
  // than the name. The address itself is what the pencil edits.
  { key: "linkedin", group: "person", label: "LinkedIn", read: ({ contact }) => (contact && contact.linkedin_url ? fullName(contact) || prettyUrl(contact.linkedin_url) : null), link: ({ contact }) => contact && contact.linkedin_url, edit: { target: "contact", field: "linkedin_url" }, source: () => chip("source unknown") },
  { key: "local_time", group: "person", label: "Timezone", read: ({ contact }) => { const z = contact && contact.local_zone; return z && z.tz ? `${z.city ? `${z.city} · ` : ""}${z.abbr || z.tz}` : null; }, edit: { target: "contact", prop: "hs_timezone" }, source: ({ contact }) => { const z = contact && contact.local_zone; return z ? chip(z.from === "contact" ? "HubSpot export" : "Computed") : null; } },
  { key: "lead_since", group: "person", label: "Lead since", read: ({ contact }) => (contact && contact.created_at ? shortDate(contact.created_at) : null), edit: null, source: () => chip("Computed") },
  { key: "last_activity", group: "person", label: "Last activity", read: ({ contact }) => (contact && contact.last_activity_at ? shortDate(contact.last_activity_at) : null), edit: null, source: () => chip("Computed") },
  { key: "kind", group: "person", label: "Lead or associated", read: ({ contact }) => (contact && contact.kind ? { lead: "Lead", associated: "Associated", junk: "Not a lead" }[contact.kind] || contact.kind : null), edit: { target: "contact", field: "kind" }, source: ({ contact }) => arrivalChip(contact, "intro") || chip("source unknown") },
  { key: "lifecycle", group: "person", label: "Lifecycle stage", read: ({ contact, company }) => (contact ? (company && company.status === "churned" ? "churned" : LIFECYCLE[contact.lifecycle_stage] || contact.lifecycle_stage) : null), edit: { target: "contact", field: "lifecycle_stage" }, source: ({ contact, timeline }) => transitionChip(lifecycleRow(contact, timeline)) },
  { key: "source", group: "person", label: "Source", read: ({ contact }) => (contact && SOURCES[contact.lead_source]) || null, edit: { target: "contact", field: "lead_source" }, source: ({ contact }) => arrivalChip(contact, "intro") || arrivalChip(contact, "form") || chip("source unknown") },
  { key: "channel", group: "person", label: "Channel", read: ({ contact }) => { const ch = contact && (contact.lead_channel || contact.lead_channel_derived); return ch ? CHANNELS[ch] || enumWords(ch) : null; }, edit: null, source: ({ contact }) => (contact && contact.lead_channel ? arrivalChip(contact, "intro") || arrivalChip(contact, "form") || chip("source unknown") : contact && contact.lead_channel_derived ? chip("HubSpot export") : null) },
  { key: "referrer", group: "person", label: "Referrer", read: ({ contact }) => (contact && contact.referrer_name) || null, edit: { target: "contact", prop: "referred_by" }, source: ({ contact }) => arrivalChip(contact, "intro") || chip("source unknown") },

  // --- Company ---
  { key: "company_name", group: "company", label: "Name", read: ({ company }) => company && company.name, edit: { target: "company", field: "name" }, source: ({ company }) => apolloChip(company) },
  // The typed website first: it is what the pencil writes (properties_set
  // company_website), and reading the domain first made a save look like it did
  // nothing on every company that has one. The domain stays the fallback, never
  // edited from here: it is what mail is matched on.
  { key: "website", group: "company", label: "Website", read: ({ company }) => (company ? prettyUrl(props(company).company_website || company.domain || "") || null : null), edit: { target: "company", prop: "company_website" }, source: ({ company }) => apolloChip(company) },
  { key: "company_linkedin", group: "company", label: "LinkedIn page", read: ({ company }) => (company && company.linkedin_url ? prettyUrl(company.linkedin_url) : null), link: ({ company }) => company && company.linkedin_url, edit: { target: "company", field: "linkedin_url" }, source: ({ company }) => apolloChip(company) },
  { key: "industry", group: "company", label: "Industry", read: ({ company }) => (company && company.industry ? enumWords(company.industry) : null), edit: { target: "company", field: "industry", fromValues: true }, source: ({ company }) => apolloChip(company) },
  { key: "employees", group: "company", label: "Employees", read: ({ company }) => company && company.employee_range, edit: { target: "company", field: "employee_range" }, source: ({ company }) => apolloChip(company) },
  { key: "country", group: "company", label: "Country", read: ({ company }) => company && company.country, edit: { target: "company", field: "country" }, source: ({ company }) => apolloChip(company) },
  { key: "city", group: "company", label: "City", read: ({ company }) => props(company).city || null, edit: { target: "company", prop: "city" }, source: () => chip("source unknown") },
  { key: "state", group: "company", label: "State", read: ({ company }) => props(company).state || null, edit: { target: "company", prop: "state" }, source: () => chip("source unknown") },
  { key: "company_timezone", group: "company", label: "Timezone", read: ({ company }) => props(company).timezone || null, edit: { target: "company", prop: "timezone" }, source: () => chip("source unknown") },
  { key: "description", group: "company", label: "Description", read: ({ company }) => props(company).company_description || props(company).description || null, edit: { target: "company", prop: "company_description" }, source: ({ company }) => (props(company).company_description ? apolloChip(company) : chip("HubSpot export")) },
  { key: "founded", group: "company", label: "Founded", read: ({ company }) => props(company).founded_year || null, edit: { target: "company", prop: "founded_year" }, source: ({ company }) => apolloChip(company) },
  {
    key: "funding",
    group: "company",
    label: "Total funding",
    read: ({ company }) => {
      const p = props(company);
      const v = !blank(p.total_funding) ? p.total_funding : p.total_money_raised;
      if (blank(v)) return null;
      const n = Number(v);
      return Number.isFinite(n) ? moneyShort(n) : String(v);
    },
    edit: { target: "company", prop: "total_funding" },
    hint: ({ score }) => {
      const c = score && Array.isArray(score.chips) ? score.chips.find((x) => x.group === "funding") : null;
      return c ? `Fit counts funding for up to ${c.ceiling} points, so “Would score” updates.` : null;
    },
    source: ({ company }) => (!blank(props(company).total_funding) ? apolloChip(company) : chip("HubSpot export")),
  },
  { key: "revenue", group: "company", label: "Revenue", read: ({ company }) => { const v = props(company).annualrevenue; return blank(v) ? null : Number.isFinite(Number(v)) ? moneyShort(Number(v)) : String(v); }, edit: null, source: () => chip("HubSpot export") },
  { key: "tech", group: "company", label: "Tech stack", read: ({ company }) => { const v = props(company).web_technologies; return blank(v) ? null : Array.isArray(v) ? v.join(", ") : String(v).replace(/;/g, ", "); }, edit: null, source: () => chip("HubSpot export") },
  { key: "keywords", group: "company", label: "Keywords", read: ({ company }) => { const v = props(company).keywords; return Array.isArray(v) && v.length ? v.join(", ") : null; }, edit: null, source: ({ company }) => apolloChip(company) },
  { key: "logo", group: "company", label: "Logo", read: ({ company }) => props(company).logo_url || null, edit: null, source: ({ company }) => apolloChip(company) },

  // --- How they arrived ---
  { key: "arrived", group: "arrived", label: "Arrived", read: ({ contact }) => (contact && contact.created_at ? shortDate(contact.created_at) : null), edit: null, source: () => chip("Computed") },
  { key: "first_form", group: "arrived", label: "Form", read: ({ form }) => (form ? formName(form) : null), edit: null, source: ({ form }) => (form ? chip(`Webflow form · ${shortDate(form.occurred_at)}`) : null) },
  { key: "intro", group: "arrived", label: "Intro", read: ({ contact }) => (contact && contact.lead_channel === "intro_email" ? `Intro email${contact.referrer_name ? ` from ${contact.referrer_name}` : ""}` : null), edit: null, source: ({ contact }) => arrivalChip(contact, "intro") },
  { key: "first_touch", group: "arrived", label: "First touch", read: ({ contact }) => (contact && contact.first_touch_source ? enumWords(contact.first_touch_source) : null), edit: null, source: () => chip("HubSpot export") },
  { key: "touch_detail", group: "arrived", label: "Touch detail", read: ({ contact }) => (contact && contact.first_touch_detail) || null, edit: null, source: () => chip("HubSpot export") },

  // --- History ---
  { key: "status", group: "history", label: "Status", read: ({ company }) => (company ? STATUS[company.status] || company.status : null), edit: null, source: ({ company }) => transitionChip(company && lastTransition(company, "status", company.status)) },
  // A client's board column is Closed won, though the win clears pipeline_stage:
  // 1f prints "Closed won · 1 day", counted from the win.
  { key: "pipeline_stage", group: "history", label: "Pipeline stage", read: ({ company, act, now }) => { if (!company) return null; if (!company.pipeline_stage) { if (company.status !== "client" || !company.won_at) return null; const w = daysBetween(company.won_at, now); return `${STAGE_LABELS.closed_won}${w !== null ? ` · ${plural(Math.max(1, w), "day")}` : ""}`; } const d = daysBetween(act && act.facts && act.facts.stage_entered_at, now); return `${STAGE_LABELS[company.pipeline_stage] || company.pipeline_stage}${d !== null ? ` · ${plural(d, "day")}` : ""}`; }, edit: null, source: ({ company }) => transitionChip(company && (company.pipeline_stage ? lastTransition(company, "pipeline_stage", company.pipeline_stage) : lastTransition(company, "status", "client"))) },
  { key: "stage_history", group: "history", label: "Stage history", read: ({ company }) => stageHistory(company), edit: null, source: () => chip("Computed") },
  { key: "status_reason", group: "history", label: "Why", read: ({ company }) => (company && company.status_reason ? enumWords(company.status_reason) : null), edit: null, source: ({ company }) => transitionChip(company && lastTransition(company, "status", company.status)) },
  { key: "poc", group: "history", label: "Point of contact", read: ({ company }) => fullName(pointOfContact(company)), edit: { target: "company", field: "primary_contact_id" }, source: () => chip("source unknown") },
  { key: "parked_until", group: "history", label: "Wakes", read: ({ company }) => (company && company.status === "recycled" && company.recycle_until ? shortDate(company.recycle_until) : null), edit: { target: "company", field: "recycle_until" }, source: () => chip("You") },
  { key: "churned_on", group: "history", label: "Churned on", read: ({ company }) => (company && company.churned_at ? shortDate(company.churned_at) : null), edit: null, source: ({ company }) => transitionChip(company && lastTransition(company, "status", "churned")) },
  { key: "earlier_stays", group: "history", label: "Earlier stays", read: ({ company }) => earlierStays(company), edit: null, source: () => chip("Computed") },

  // --- LinkedIn ads ---
  // The audit flag Gabriel sets by hand (migration 043). Behind the scenes asks
  // "Mark audited" only while it is a prospect's question; what an audit found
  // stays readable here once the company is won, parked or lost.
  { key: "audited_at", group: "linkedin", label: "Audited on", read: ({ company }) => (company && company.audited_at ? shortDate(company.audited_at) : null), edit: { target: "company", field: "audited_at" }, source: () => chip("You") },
  { key: "audit_notes", group: "linkedin", label: "Audit notes", read: ({ company }) => (company && company.audit_notes ? String(company.audit_notes).trim() || null : null), edit: { target: "company", field: "audit_notes" }, source: () => chip("You") },

  // --- HubSpot export (frozen) ---
  { key: "hubspot_first_conversion", group: "hubspot", label: "First form", read: ({ contact }) => props(contact).first_conversion_event_name || null, edit: null, source: () => chip("HubSpot export") },
  { key: "hubspot_first_conversion_date", group: "hubspot", label: "First form date", read: ({ contact }) => (props(contact).first_conversion_date ? shortDate(props(contact).first_conversion_date) : null), edit: null, source: () => chip("HubSpot export") },
  { key: "hubspot_first_page", group: "hubspot", label: "First page", read: ({ contact }) => props(contact).hs_analytics_first_url || null, edit: null, source: () => chip("HubSpot export") },
  { key: "hubspot_first_referrer", group: "hubspot", label: "First referrer", read: ({ contact }) => props(contact).hs_analytics_first_referrer || null, edit: null, source: () => chip("HubSpot export") },
  { key: "hubspot_source", group: "hubspot", label: "Analytics source", read: ({ contact }) => (props(contact).hs_analytics_source ? enumWords(props(contact).hs_analytics_source) : null), edit: null, source: () => chip("HubSpot export") },
  { key: "hubspot_visits", group: "hubspot", label: "Visits", read: ({ contact }) => (blank(props(contact).hs_analytics_num_visits) ? null : String(props(contact).hs_analytics_num_visits)), edit: null, source: () => chip("HubSpot export") },
  { key: "hubspot_deal_amount", group: "hubspot", label: "Old deal amount", read: ({ contact }) => { const v = props(contact).recent_deal_amount; return blank(v) ? null : Number.isFinite(Number(v)) ? moneyShort(Number(v)) : String(v); }, edit: null, source: () => chip("HubSpot export") },
  { key: "hubspot_close_date", group: "hubspot", label: "Old close date", read: ({ contact }) => (props(contact).closedate ? shortDate(props(contact).closedate) : null), edit: null, source: () => chip("HubSpot export") },
];

function formName(form) {
  const meta = (form && form.metadata) || {};
  const name = (meta.form && (meta.form.form_name || meta.form.name)) || meta.form_name || form.title || "Website form";
  return `${name} · ${shortDate(form.occurred_at)}`;
}

/** "New Sep 15 → Call scheduled Sep 16 → Agreement + legal Sep 27 → Closed won Sep 30". */
export function stageHistory(company) {
  const rows = ((company && company.status_history) || [])
    .filter((r) => r && (r.field === "pipeline_stage" || (r.field === "status" && r.to_value === "client")))
    .slice()
    .sort((a, b) => toDate(a.occurred_at) - toDate(b.occurred_at));
  const parts = [];
  for (const r of rows) {
    const label = r.field === "status" ? "Closed won" : STAGE_LABELS[r.to_value];
    if (!label) continue;
    const text = `${label} ${shortDate(r.occurred_at)}`;
    if (parts[parts.length - 1] && parts[parts.length - 1].startsWith(label)) continue;
    parts.push(text);
  }
  return parts.length ? parts.join(" → ") : null;
}

/** "First time here", or how far the earlier stays got. Null before Feb 2026's stints. */
export function earlierStays(company) {
  const stints = (company && company.pipeline_stints) || [];
  if (!stints.length) return null;
  const earlier = stints.slice(1);
  if (!earlier.length) return "First time here";
  const furthest = earlier.map((s) => STAGE_LABELS[s.furthest_stage] || s.furthest_stage).filter(Boolean);
  return `${plural(earlier.length, "earlier stay")}${furthest.length ? ` · got as far as ${furthest[0]}` : ""}`;
}

/* ------------------------------------------------------------ the tab */

/**
 * The tab, grouped:
 *   groups  [{ id, label, title, count, rows, summary, oneLine }]
 *           rows: [{ key, label, value, source, edit, link?, hint? }]
 *   facts   how many fields hold a value ("Search 31 facts")
 *   empty   how many registry fields are empty ("Show empty (24)")
 * `query` filters rows by label or value; `showEmpty` adds the empty rows back.
 */
export function profileModel({ contact = null, company = null, act = null, timeline = [], score = null, query = "", showEmpty = false, situation = null, now = new Date() } = {}) {
  const form = latestFormSubmission(timeline);
  const ctx = { contact, company, act, timeline, score, form, now };
  const q = String(query || "").trim().toLowerCase();

  const rows = FIELDS.map((f) => {
    const value = f.read(ctx);
    const empty = blank(value);
    return {
      key: f.key,
      group: f.group,
      label: f.label,
      value: empty ? null : String(value),
      empty,
      source: empty ? null : f.source ? f.source(ctx) : null,
      edit: f.edit || null,
      link: f.link ? f.link(ctx) || null : null,
      hint: f.hint ? f.hint(ctx) : null,
    };
  });

  // What they told us: the form's own answers, each its own fact.
  const told = form
    ? formAnswers(form).map((a) => ({ key: `form_${a.key}`, group: "told", label: a.label, value: a.value, empty: false, source: { text: `Webflow form · ${shortDate(form.occurred_at)}` }, edit: null }))
    : [];
  const message = form ? formMessage(form) : null;
  if (message) told.unshift({ key: "form_message", group: "told", label: "Message", value: message, empty: false, source: { text: `Webflow form · ${shortDate(form.occurred_at)}` }, edit: null });

  // Deal and money, from the same rows the rail shows, each with its source.
  const deal = company
    ? dealRows({ company, act, timeline, situation, now })
        .filter((r) => !r.empty && r.value && r.value !== "not won yet")
        .map((r) => ({
          key: `deal_${r.key}`,
          group: "deal",
          label: r.key === "budget" ? "Their ad budget" : r.key === "fee" ? "Fee" : r.label,
          value: r.lines && r.lines.length ? r.lines.join(" · ") : r.value,
          empty: false,
          source: { text: dealSource(r, company) },
          edit: r.key === "budget" ? { target: "company", field: "monthly_value" } : r.key === "fee" ? { target: "company", field: "signed_fee_structure" } : r.key === "negotiated" ? { target: "company", field: "signed_terms_note" } : r.key === "won" ? { target: "company", field: "won_at" } : null,
        }))
    : [];

  const all = [...rows, ...told, ...deal];
  const facts = all.filter((r) => !r.empty).length;
  const empty = rows.filter((r) => r.empty).length;

  const match = (r) => !q || r.label.toLowerCase().includes(q) || String(r.value || "").toLowerCase().includes(q);
  const groups = GROUPS.map((g) => {
    const mine = all.filter((r) => r.group === g.id);
    const visible = mine.filter((r) => (showEmpty || !r.empty) && match(r));
    return {
      id: g.id,
      label: g.label,
      title: g.id === "person" && contact ? `Person · ${fullName(contact) || ""}`.trim() : g.id === "company" && company ? `Company · ${company.name}` : g.label,
      count: mine.filter((r) => !r.empty).length,
      rows: visible,
      summary: groupSummary(g.id, { contact, company, act, form, score }),
      // An empty group is one line, not an empty card.
      oneLine: visible.length === 0,
    };
  }).filter((g) => !q || g.rows.length);

  return {
    groups,
    facts,
    empty,
    searchLabel: `Search ${plural(facts, "fact")}`,
    showEmptyLabel: `Show empty (${empty})`,
  };
}

function dealSource(row, company) {
  if (row.key === "budget") {
    const s = company && company.money_source;
    if (s && s.evidence_source) {
      const what = { fireflies: "Fireflies", gmail: "Gmail", auditor: "Auditor", webflow_form: "Webflow form", landing_page: "Landing page", calendar: "Calendar" }[s.evidence_source] || "source unknown";
      return s.effective_date ? `${what} · ${shortDate(s.effective_date)}` : what;
    }
    return "source unknown";
  }
  if (row.key === "quoted") return row.quote ? `Gmail · ${shortDate(row.quote.at)}` : "Computed";
  if (row.key === "won") return "You";
  if (row.key === "agreement") return row.link && row.link.kind === "notice" ? "Gmail" : "You";
  return "You";
}

/** The one line an empty, or naturally short, group reads as. */
function groupSummary(id, { contact, company, act, form, score }) {
  switch (id) {
    case "arrived": {
      if (contact && contact.lead_channel === "intro_email") return `intro email${contact.referrer_name ? ` from ${contact.referrer_name}` : ""}${contact.created_at ? `, ${shortDate(contact.created_at)}` : ""}`;
      if (form) return `the form, ${shortDate(form.occurred_at)}`;
      return null;
    }
    case "told":
      if (form) return null;
      return contact && contact.lead_channel === "intro_email" ? "none, no form: they came by intro" : "none, no form";
    case "linkedin":
      return act && act.ads ? null : "no ad account linked";
    case "hubspot":
      return contact && contact.hubspot_id ? null : "none, arrived after we left HubSpot";
    case "other":
      return score && score.score !== undefined && score.score !== null ? `would score ${score.score} today` : null;
    default:
      return null;
  }
}
